<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb />
      </div>
    </div>
    <b-card>
      <div class="row d-flex mb-2 justify-content-between align-items-center">
        <h4 class="col-12 col-xl-auto mb-2 mb-xl-0 font-weight-bold">點數管理</h4>
        <div class="col-12 col-xl-auto d-flex flex-column align-items-end">
          <div class="mb-2" v-if="query.merchant_id">
            <b-button
              class="flex-shrink-0"
              variant="primary"
              @click="handleExport"
            >
              <span class="mdi mdi-download"></span> 匯出 CSV
            </b-button>
          </div>
          <div class="d-flex flex-wrap justify-content-end">
            <b-button
              class="mb-2 mb-xl-0 mr-2"
              variant="link"
              :to="{
                name: 'PersonalPoint',
              }"
            >
              <i class="fa fa-folder-open"></i>點數異動人員
            </b-button>
            <b-button
              class="mb-2 mb-xl-0 mr-2"
              variant="link"
              :to="{
                name: 'PointLogList',
              }"
            >
              <i class="fa fa-folder-open"></i>點數異動紀錄
            </b-button>
            <b-button
              v-if="$permissions.has($permissions.consts.POINT_LOGS_IMPORT)"
              class="flex-shrink-0 mb-2 mb-xl-0 mr-2"
              variant="inverse-info"
              :to="{ name: 'PointImport' }"
            >
              <span class="mdi mdi-cloud-upload"></span> 批次上傳
            </b-button>
            <b-button
              v-if="$permissions.has($permissions.consts.POINT_LIST_CREATE)"
              class="flex-shrink-0 mb-2 mb-xl-0 mr-2"
              variant="primary"
              :to="{ name: 'PointCreate' }"
            >
              <i class="fa fa-plus"></i>新增點數
            </b-button>
            <b-button
              v-if="$permissions.has($permissions.consts.POINT_LIST_DEDUCT)"
              class="flex-shrink-0 mb-2 mb-xl-0"
              variant="primary"
              :to="{ name: 'PointUse' }"
            >
              <i class="fa fa-minus"></i>扣除點數
            </b-button>
          </div>
        </div>
      </div>

      <div class="row mx-0 mb-2">
        <b-button v-b-toggle.collapse-filter variant="outline-info">
          <span class="mdi mdi-magnify"></span>進階搜尋
        </b-button>
      </div>

      <b-collapse id="collapse-filter" v-model="isAdvancedSearchVisible" class="mb-2">
        <b-card>
          <b-form>
            <b-form-group
              label="通路名稱"
              label-cols-sm="2"
              label-cols-lg="1"
              content-cols-sm
              content-cols-lg="3"
            >
              <b-form-select
                v-model="query.merchant_id"
                :options="merchants"
                :disabled="merchants.length == 2"
                @change="handlePageReset"
              ></b-form-select>
            </b-form-group>

            <b-form-group
              label="點數狀態"
              label-cols-sm="2"
              label-cols-lg="1"
              content-cols-sm
              content-cols-lg="3"
            >
              <b-form-select
                v-model="query.validity_status"
                :options="validityStatusOptions"
                @change="handlePageReset"
              ></b-form-select>
            </b-form-group>
          </b-form>
        </b-card>
      </b-collapse>

      <b-card class="mb-2">
        <b-input-group>
          <b-form-input
            placeholder="搜尋會員名稱、手機、外部編碼"
            v-model="query.keyword"
            v-on:keyup.enter="handleSearch()"
          ></b-form-input>
          <b-input-group-append>
            <b-button @click="handleSearch()">
              <i class="fa fa-search"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-card>
      <b-table
        striped
        hover
        responsive
        :items="points"
        :fields="fields"
        :busy="isLoading"
        :no-local-sorting="true"
        @sort-changed="handleSort"
      >
        <template #cell(validity_status)="data">
          <b-badge v-if="data.item.validity_status === 'active'" variant="success">生效中</b-badge>
          <b-badge v-else-if="data.item.validity_status === 'pending'" variant="warning" class="text-white">未生效</b-badge>
          <b-badge v-else-if="data.item.validity_status === 'expired'" variant="danger">已失效</b-badge>
          <span v-else>-</span>
        </template>
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template #cell(actions)="data">
          <b-button
            class="mr-1"
            variant="inverse-primary"
            :to="{
              name: 'PointView',
              params: {
                point_id: data.item.id,
                merchant_id: query.merchant_id,
              },
            }"
          >
            <span class="mdi mdi-eye"></span> 查看
          </b-button>
          <b-button
            v-if="$permissions.has($permissions.consts.POINT_LIST_MODIFY)"
            class="mr-1"
            variant="inverse-warning"
            @click.prevent
            style="opacity: 0.65; cursor: not-allowed;"
            v-b-tooltip.hover
            title="請透過『新增點數』或『扣除點數』按鈕，調整會員點數"
          >
            <span class="mdi mdi-lead-pencil"></span> 編輯
          </b-button>
          <b-button
            v-if="$permissions.has($permissions.consts.POINT_LIST_DELETE)"
            variant="inverse-danger"
            @click="deletePoint(data.item)"
          >
            <span class="mdi mdi-delete"></span> 刪除
          </b-button>
        </template>
      </b-table>

      <CustomPagination
        :currentPage="query.page"
        :totalRows="total"
        :perPage="query.per_page"
        :perPageOptions="perPageOption"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import { mapState } from "vuex";
import axios from "axios";
import store from "@/store";
import pointApi from "../../../apis/point";
import merchantApi from "../../../apis/merchant";
import * as consts from "@/consts";
import moment from "moment";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: { CustomPagination, BreadCrumb },
  data() {
    return {
      consts: consts,
      isLoading: false,
      isAdvancedSearchVisible: false,
      points: [],
      merchants: [{ value: null, text: "請選擇" }],
      validityStatusOptions: [
        { value: null, text: "全部" },
        { value: "pending", text: "未生效" },
        { value: "active", text: "生效中" },
        { value: "expired", text: "已失效" },
      ],
      fields: [
        {
          key: "customer_name",
          label: "會員名稱",
        },
        {
          key: "validity_status",
          label: "狀態",
        },
        {
          key: "point",
          label: "點數",
        },
        {
          key: "used_point",
          label: "已用點數",
        },
        {
          key: "created_at",
          label: "入點時間",
          sortable: true,
          formatter: (value) => {
            if (value) {
              return moment(value).format("YYYY-MM-DD HH:mm");
            }
          },
        },
        {
          key: "start_at",
          label: "點數可用日",
          sortable: true,
          formatter: (value) => {
            if (value) {
              return moment(value).format("YYYY-MM-DD HH:mm");
            }
          },
        },
        {
          key: "expired_at",
          label: "點數過期日",
          sortable: true,
          formatter: (value) => {
            if (value) {
              return moment(value).format("YYYY-MM-DD HH:mm");
            }
          },
        },
        {
          key: "actions",
          label: "管理",
        },
      ],
      currentPage: 1,
      total: 0,
      initialized: false,
      perPageOption: [
        { text: 10, value: 10 },
        { text: 20, value: 20 },
        { text: 50, value: 50 },
        { text: 100, value: 100 },
      ],
      query: {
        page: 1,
        per_page: 10,
        keyword: "",
        sort_by: "created_at",
        order_by: "desc",
        merchant_id: null,
        validity_status: null,
      },
    };
  },
  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
      defaultMerchantType: (state) => state.defaultMerchantType,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
  },
  async created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  mounted() {
    this.fetchMerchants();
    this.initialized = true;
    this.checkAndExpandAdvancedSearch();
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route(to) {
      if (!this.initialized) return;
      this.query = updateQueryFromRoute(to, this.$store, this.query);
      this.fetchPoints();
      this.checkAndExpandAdvancedSearch();
    },
  },
  methods: {
    checkAndExpandAdvancedSearch() {
      // 當有 merchant_id 或 validity_status 參數時，自動展開進階搜尋
      const hasAdvancedParams = this.$route.query.merchant_id || this.$route.query.validity_status;

      if (hasAdvancedParams && !this.isAdvancedSearchVisible) {
        this.$nextTick(() => {
          this.isAdvancedSearchVisible = true;
        });
      }
    },
    async fetchPoints() {
      if (!this.query.merchant_id) return;
      try {
        this.isLoading = true;
        const params = Object.keys(this.query).reduce((acc, key) => {
          if (this.query[key] !== null && this.query[key] !== "") {
            acc[key] = this.query[key];
          }
          return acc;
        }, {});

        const { data } = await pointApi.getPoints(this.query.merchant_id, params);
        this.total = data.meta.total;
        this.points = data.data;
      } catch (error) {
        console.error(error);
      }
      this.isLoading = false;
    },
    async fetchMerchants() {
      const { data } = await merchantApi.list();

      this.merchants = [
        { value: null, text: "請選擇" },
        ...data.data.map((merchant) => {
          return {
            value: merchant.id,
            text: `${merchant.name} (${merchant.type_name})`,
          };
        }),
      ];

      if (!this.query.merchant_id) {
        const merchants = data.data;
        const defaultMerchant =
          merchants.find(
            (merchant) => merchant.type === this.defaultMerchantType
          ) ?? merchants.find((merchant) => merchant.type === consts.MERCHANT_TYPES.LINE_TYPE);

        if (defaultMerchant) {
          this.query.merchant_id = defaultMerchant.id;
        }
      }

      if (this.query.merchant_id) {
        await this.fetchPoints();
      }
    },
    handleSort(ctx) {
      this.query.sort_by = ctx.sortBy;
      this.query.order_by = ctx.sortDesc ? "desc" : "asc";
      this.handlePageReset();
    },
    async deletePoint(point) {
      await this.$swal
        .fire({
          title: "你確定要刪除嗎？",
          html: `
            <div class="d-block">
              <div>刪除後，相關資料將無法回復，<br/>請您確認！</div>
            </div>`,
          type: "warning",
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        })
        .then(async (result) => {
          if (result.value) {
            let response = await pointApi.removePoint(point.merchant_id, point);
            if (response.status === 200) {
              this.$swal.fire({
                title: "成功",
                type: "success",
                text: "成功刪除 ",
              });
              this.fetchPoints();
            } else {
              if (response.data.message) {
                this.$swal.fire({
                  title: "錯誤",
                  type: "error",
                  text: response.data.message,
                });
              }
            }
          }
        });
    },
    handlePageChange(newPage) {
      this.query.page = newPage;
    },
    handlePerPageChange(newPerPage) {
      this.query.per_page = newPerPage;
      this.query.page = 1;
    },
    handleSearch() {
      if (!this.query.merchant_id) return;
      this.handlePageReset();
    },
    handlePageReset() {
      const currentPage = parseInt(this.query.page, 10);

      if (currentPage === 1) {
        updateUrl(this.query, this.$store, this.$router);
      } else {
        this.query.page = 1;
      }
    },
    handleExport() {
      if (!this.query.merchant_id) return;

      const baseUrl = `${process.env.VUE_APP_API_BASE_URL}/admin/organizations/${this.organization.id}/merchants/${this.query.merchant_id}/points/export`;
      const params = new URLSearchParams();
      params.append("token", store.state.auth.token);
      if (this.query.keyword) {
        params.append("keyword", this.query.keyword);
      }
      if (this.query.validity_status) {
        params.append("validity_status", this.query.validity_status);
      }
      if (this.query.sort_by) {
        params.append("sort_by", this.query.sort_by);
      }
      if (this.query.order_by) {
        params.append("order_by", this.query.order_by);
      }

      axios.get(`${baseUrl}?${params.toString()}`);

      this.$swal({
        title: "成功",
        text: "可至“我的主控台”查看紀錄及下載檔案",
        showCancelButton: true,
        cancelButtonText: "確認",
        type: "success",
        confirmButtonText: "前往查看",
      }).then((result) => {
        if (result.value) {
          this.$router.push({
            name: "MyConsoleDownloadFileList",
            params: { org_code: this.$route.params.org_code },
          });
        }
      });
    },
  },
};
</script>
